import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContextType, User } from '../types';
import { apiService } from '../services/api';
import { API_CONFIG } from '../config/api';
import logger from '../utils/logger';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation(); // Track route changes

  // Memoized auth check function
  const checkAuth = useCallback(async () => {
    setIsLoading(true); // Show loading spinner

    try {
      logger.debug('Checking authentication status');

      // Call backend to validate httpOnly cookie and get user info
      const userInfo = await apiService.getCurrentUser();

      if (userInfo) {
        // Map backend UserInfo to frontend User type
        const mappedUser: User = {
          id: userInfo.sub,
          name: userInfo.name || userInfo.email || 'Unknown User',
          email: userInfo.email || '',
          role: userInfo.roles[0] || 'CSR', // Use first role or default to CSR
        };

        setUser(mappedUser);
        setIsLoading(false); // ✅ Only hide spinner when authenticated!
      } else {
        // Not authenticated - redirect to backend login
        // Keep spinner showing during redirect!
        logger.debug('Not authenticated, redirecting to login');
        const returnTo = encodeURIComponent(window.location.href);
        window.location.href = `${API_CONFIG.BASE_URL}/api/auth/login?redirect_url=${returnTo}`;
      }
    } catch (error) {
      // On error, also redirect to login
      // Keep spinner showing during redirect!
      logger.error('Auth check failed:', error);
      const returnTo = encodeURIComponent(window.location.href);
      window.location.href = `${API_CONFIG.BASE_URL}/api/auth/login?redirect_url=${returnTo}`;
    }
  }, []);

  // Check auth on app load AND on every route change
  useEffect(() => {
    logger.debug('Route changed, checking auth');
    checkAuth(); // Always shows loading spinner during check
  }, [location.pathname, checkAuth]);

  const login = async (email: string, password: string): Promise<void> => {
    const returnTo = encodeURIComponent(window.location.href);
    window.location.href = `${API_CONFIG.BASE_URL}/api/auth/login?redirect_url=${returnTo}`;
  };

  const logout = () => {
    setUser(null);
    // Redirect to backend logout (will clear httpOnly cookie)
    // window.location.href = `${window.location.protocol}//${window.location.hostname}:8000/api/auth/logout`;  // TESTING: Commented out for HTTPS proxy testing
    window.location.href = `${API_CONFIG.BASE_URL}/api/auth/logout`;
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600 text-lg">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
